import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { Tag, type TagKind } from "../components/ds";

type Credibility = "Peer-reviewed / authoritative" | "Widely used / community standard" | "Specification";

type Source = {
  name: string;
  what: string;
  provider: string;
  resolution: string;
  coverage: string;
  temporal: string;
  credibility: Credibility;
  citation?: string;
  url: string;
  notes?: ReactNode;
};

const SOURCES: Source[] = [
  {
    name: "TerraClimate",
    what: "Monthly climate (precipitation, min/max/mean temperature, evapotranspiration, climatic water deficit, soil moisture, etc.). Ingest aggregates to annual rasters in COG.",
    provider: "Climatology Lab, University of California Merced; Northwest Knowledge Network (NKN), University of Idaho",
    resolution: "~4 km (1/24°)",
    coverage: "Global land surface",
    temporal: "1958 – present (annual update)",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Abatzoglou, J. T., Dobrowski, S. Z., Parks, S. A., & Hegewisch, K. C. (2018). " +
      "TerraClimate, a high-resolution global dataset of monthly climate and climatic water balance from 1958–2015. " +
      "Scientific Data, 5, 170191. https://doi.org/10.1038/sdata.2017.191",
    url: "https://www.climatologylab.org/terraclimate.html",
    notes: (
      <>
        Combines the climatology of WorldClim with the temporal coverage of CRU Ts4.0 and JRA-55. Excellent for
        long-term trend and SOC-model context; not a substitute for a station network when short-timescale
        accuracy is required.
      </>
    ),
  },
  {
    name: "ERA5-Land (ECMWF / Copernicus)",
    what: "High-resolution land-only reanalysis at ~9 km (0.1°), hourly since 1950, with a ~5-day publication lag. mef-agroref ingests ERA5-Land for both annual aggregates (alongside TerraClimate) and a per-day endpoint at /api/weather/daily.",
    provider: "European Centre for Medium-Range Weather Forecasts (ECMWF) — distributed via Copernicus Climate Change Service (C3S)",
    resolution: "~9 km (0.1°)",
    coverage: "Global land surface",
    temporal: "1950 – present (~5-day lag behind real time)",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Muñoz Sabater, J. (2019). ERA5-Land hourly data from 1950 to present. " +
      "Copernicus Climate Change Service (C3S) Climate Data Store (CDS). " +
      "DOI: 10.24381/cds.e2161bac.",
    url: "https://cds.climate.copernicus.eu/datasets/reanalysis-era5-land",
    notes: (
      <>
        <p className="mb-3">
          Variables are downloaded as hourly NetCDF slices and reduced to daily and annual COGs
          at ingest. The table below shows native units on disk and the reduction applied:
        </p>

        <table className="mb-4 w-full text-[12px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="py-1 pr-3 font-medium">Variable</th>
              <th className="py-1 pr-3 font-medium">Code</th>
              <th className="py-1 pr-3 font-medium">Native unit</th>
              <th className="py-1 font-medium">Reduction (hourly → daily)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3">Precipitation</td>
              <td className="py-1.5 pr-3 font-mono">tp</td>
              <td className="py-1.5 pr-3">m/day</td>
              <td className="py-1.5">accumulated; value at hr=23 UTC</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3">2 m temperature</td>
              <td className="py-1.5 pr-3 font-mono">t2m</td>
              <td className="py-1.5 pr-3">K</td>
              <td className="py-1.5">arithmetic mean of 24 hourly samples</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3">Potential evaporation</td>
              <td className="py-1.5 pr-3 font-mono">pev</td>
              <td className="py-1.5 pr-3">m/day (negative)</td>
              <td className="py-1.5">accumulated; value at hr=23 UTC</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3">Solar radiation downwards</td>
              <td className="py-1.5 pr-3 font-mono">ssrd</td>
              <td className="py-1.5 pr-3">J/m²/day</td>
              <td className="py-1.5">accumulated; value at hr=23 UTC</td>
            </tr>
          </tbody>
        </table>

        <p className="mb-2">
          The <code>/api/weather/daily</code> response converts to agronomic units; the formulas
          are also surfaced in <code>provenance.transforms</code>:
        </p>

        <table className="mb-4 w-full text-[12px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="py-1 pr-3 font-medium">JSON field</th>
              <th className="py-1 pr-3 font-medium">From</th>
              <th className="py-1 font-medium">Formula</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3 font-mono">rain_m</td>
              <td className="py-1.5 pr-3 font-mono">tp</td>
              <td className="py-1.5">identity</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3 font-mono">temp_c</td>
              <td className="py-1.5 pr-3 font-mono">t2m</td>
              <td className="py-1.5">t2m − 273.15</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3 font-mono">et0_m</td>
              <td className="py-1.5 pr-3 font-mono">pev</td>
              <td className="py-1.5">pev × −1, clip ≥ 0</td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 pr-3 font-mono">rg_mj_m2</td>
              <td className="py-1.5 pr-3 font-mono">ssrd</td>
              <td className="py-1.5">ssrd × 1e-6</td>
            </tr>
          </tbody>
        </table>

        <p className="rounded-xs border-[1.2px] border-mef-separator bg-mef-subtle px-3 py-2 text-[12px]">
          <strong>Publication lag:</strong> ERA5-Land lags real-time by ~5 days. Queries with{" "}
          <code>date_end &gt; today − 5 days</code> return warnings for missing days, not errors.
        </p>
      </>
    ),
  },
  {
    name: "SoilGrids 2.0",
    what: "Global gridded predictions of seven soil properties (bulk density, organic carbon, pH, cation exchange capacity, clay/silt/sand fractions, coarse fragments, nitrogen) at multiple depth intervals.",
    provider: "ISRIC — World Soil Information",
    resolution: "250 m",
    coverage: "Global land surface (excluding Antarctica)",
    temporal: "Static snapshot (2020 release; refreshed irregularly)",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Poggio, L., de Sousa, L. M., Batjes, N. H., Heuvelink, G. B. M., Kempen, B., Ribeiro, E., & Rossiter, D. (2021). " +
      "SoilGrids 2.0: producing soil information for the globe with quantified spatial uncertainty. " +
      "SOIL, 7, 217–240. https://doi.org/10.5194/soil-7-217-2021",
    url: "https://www.isric.org/explore/soilgrids",
    notes: (
      <>
        The primary reference grid for most soil-property layers served by this API. Uncertainty maps are
        published alongside the mean predictions; layers in <code>mef-agroref</code> currently expose only the
        mean, but the uncertainty grids are available from ISRIC if your model needs them.
      </>
    ),
  },
  {
    name: "LUCAS / ESDAC topsoil (Europe)",
    what: "Alternative European soil property bundle covering 0-20 cm topsoil: pH (H₂O and CaCl₂), N, P, K, CaCO₃, CEC, C:N ratio, and physical properties (clay/sand/silt, coarse fragments, bulk density). Selectable as an alternative provider for /api/soil/* via the source toggle on the Explore page (or ?source=lucas in the API).",
    provider: "European Soil Data Centre (ESDAC), Joint Research Centre — derived from the LUCAS topsoil survey (Ballabio et al. 2016 / 2019).",
    resolution: "500 m (ETRS89 / LAEA Europe at ingest; re-projected to EPSG:4326 for API reads)",
    coverage: "EU-27 + UK + EEA (continental Europe). Outside this footprint, queries return null and the SoilGrids source remains the right choice.",
    temporal: "Static (chemical properties: 2015; physical properties: 2008–2015 LUCAS survey)",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Ballabio, C., Panagos, P., & Monatanarella, L. (2016). Mapping topsoil physical properties at European scale using the LUCAS database. Geoderma 261, 110–123. " +
      "Ballabio, C., Lugato, E., Fernández-Ugalde, O., Orgiazzi, A., Jones, A., Borrelli, P., Montanarella, L., & Panagos, P. (2019). Mapping LUCAS topsoil chemical properties at European scale using Gaussian process regression. Geoderma 355, 113912.",
    url: "https://esdac.jrc.ec.europa.eu/",
    notes: (
      <>
        <strong>Operator-supplied data.</strong> ESDAC requires you to register (free, individual licence)
        and request the specific products before download. Once obtained, the per-variable zips are dropped
        into <code>/data/_downloads/lucas/</code> on the API host and the
        {" "}<code>update_lucas</code>{" "}ingest unpacks, re-projects, and writes COGs registered under
        <code> source=lucas</code>. See <code>README.md §LUCAS / ESDAC manual ingest</code> for the
        registration steps and the exact filenames expected. Values are re-scaled at ingest to match
        SoilGrids semantics (e.g. clay/sand/silt in g/kg) so the SoilGrids-aware texture classifier and UI
        keep working unchanged.
      </>
    ),
  },
  {
    name: "INRAE high-resolution soil maps (France)",
    what: "Higher-resolution soil property rasters covering metropolitan France, served as an INRAE WMS. Used to overlay French pixels onto the global composite for properties (notably CaCO3) where INRAE's data is materially better than SoilGrids.",
    provider: "INRAE — Institut national de recherche pour l'agriculture, l'alimentation et l'environnement",
    resolution: "~250 m (varies by property)",
    coverage: "Metropolitan France",
    temporal: "Static snapshot; updated by INRAE on irregular cadence",
    credibility: "Peer-reviewed / authoritative",
    url: "https://www.inrae.fr/",
    notes: (
      <>
        Layered in by the <code>overlay_soil_layer</code> ingest step, which marks the affected pixels with
        <em> source id 6 </em>(see the "Source &amp; provenance" section below). Outside France, the global
        composite falls back to SoilGrids and admin-level averages.
      </>
    ),
  },
  {
    name: "IPCC climate zones & soil types (JRC ESDAC)",
    what: "Two categorical global rasters: 12 IPCC climate zones (Tropical Wet/Moist/Dry/Montane, Warm/Cool Temperate Moist/Dry, Boreal Moist/Dry, Polar Moist/Dry) and 8 IPCC major soil types (HAC, LAC, Sandy, Spodic, Volcanic, Wetland, Organic, plus an 'Other Areas' fill class). Surfaced as `ipcc_climate` and `ipcc_soil` in the API.",
    provider: "European Commission Joint Research Centre — European Soil Data Centre (ESDAC); derived from the Harmonized World Soil Database (HWSD) and an IPCC 3A.5 climate-zone construction over CRU+WorldClim+JRA-55 inputs.",
    resolution: "5 arc-minutes (~10 km at the equator)",
    coverage: "Global",
    temporal: "Static snapshot (2010 JRC publication); methodology stable across re-releases.",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Hiederer, R., Köchy, M. (2011). Global Soil Organic Carbon Estimates and the Harmonized World Soil Database. " +
      "JRC Scientific and Technical Reports, EUR 25225 EN. " +
      "Classification per IPCC (2006) Guidelines for National GHG Inventories, Volume 4 — " +
      "Chapter 3 Annex 3A.5 (climate) and Chapter 2 Table 2A.2 (soil).",
    url: "https://esdac.jrc.ec.europa.eu/content/support-renewable-energy-directive",
    notes: (
      <>
        These two layers are what most peer-reviewed soil-carbon and GHG-inventory work cites. Free to
        download (no registration), CC-BY-equivalent. Ingested by <code>update_ipcc</code>; cached zips
        live in <code>/data/_downloads/ipcc/</code>. Pair them with the CFT layers below if you need
        the labels used by Cool Farm Tool / Cool Farm Platform directly.
      </>
    ),
  },
  {
    name: "Cool Farm Tool / Cool Farm Platform (CFT/CFP)",
    what: "Categorical climate (10 classes) and soil (6 classes) rasters using the Cool Farm Alliance's published classification — the same labels CFT users see when entering farm data. Surfaced as `cft_climate` and `cft_soil` in the API.",
    provider: "Cool Farm Alliance (Cool Farm Tool / Cool Farm Platform)",
    resolution: "5 arc-minutes (~10 km at the equator) — same grid as IPCC; the encoding is renumbered to match CFT's docs order.",
    coverage: "Global. CFT's 10 climate classes cover farming areas only; the polar pixels (CFT class 11/12) carry IPCC labels (Polar Moist / Polar Dry) since CFT doesn't define them.",
    temporal: "Static; refreshed when CFA publishes an updated raster.",
    credibility: "Widely used / community standard",
    url: "https://app.coolfarmtool.org/docs/reference-data/tables/climate.html",
    notes: (
      <>
        CFA's rasters aren't publicly downloadable, so the TIFs are vendored into the backend source tree
        (<code>backend/apps/maintenance/seed_data/cft/</code>, ~1 MB) and ingested by{" "}
        <code>update_cft</code> on first boot. The labels match{" "}
        <a href="https://app.coolfarmtool.org/docs/reference-data/tables/climate.html" target="_blank" rel="noopener noreferrer">
          CFT's climate table
        </a>{" "}
        and{" "}
        <a href="https://app.coolfarmtool.org/docs/reference-data/tables/soil-characteristics.html" target="_blank" rel="noopener noreferrer">
          soil-characteristics table
        </a>{" "}
        verbatim, so a query result you can paste directly into a CFT model run. The class boundaries
        themselves derive from the same IPCC methodology — pick whichever scheme your downstream
        consumer expects; for most agronomic / SOC modelling that's CFT.
      </>
    ),
  },
  {
    name: "Hansen Global Forest Change (v1.11)",
    what: "Per-pixel tree-cover fraction for the reference year 2000 (treecover_2000_pct) plus the calendar year each pixel first dropped below the forest threshold (lossyear, 2001–2023). Ingested as two COGs; surfaced through /api/landuse/point and /api/landuse/zone.",
    provider: "Hansen / UMD / Google / USGS / NASA — Global Land Analysis & Discovery (GLAD) lab, University of Maryland",
    resolution: "~30 m (1 arc-second)",
    coverage: "Global land surface",
    temporal: "Baseline year 2000; annual forest-loss layers 2001 – 2023 (v1.11)",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Hansen, M. C., Potapov, P. V., Moore, R., Hancher, M., et al. (2013). " +
      "High-Resolution Global Maps of 21st-Century Forest Cover Change. " +
      "Science, 342(6160), 850–853. https://doi.org/10.1126/science.1244693",
    url: "https://glad.earthengine.app/view/global-forest-change",
    notes: (
      <>
        The load-bearing input for <strong>ISO 14067 §6.4.9 direct land-use-change (dLUC)</strong>{" "}
        accounting: <code>treecover_2000_pct</code> is the reference-year forest baseline and{" "}
        <code>lossyear</code> records when each pixel lost forest. <code>/api/landuse/zone</code> returns a
        per-year loss histogram with EUDR-cutoff aggregates over the admin polygon. See the{" "}
        <em>Land use &amp; forest change</em> section below for how it pairs with WorldCover and HGB.
      </>
    ),
  },
  {
    name: "ESA WorldCover (v200, 2021)",
    what: "Global 10 m land-cover classification with 11 classes (Tree cover, Shrubland, Grassland, Cropland, Built-up, Bare / sparse vegetation, Snow & ice, Permanent water, Herbaceous wetland, Mangroves, Moss & lichen). Surfaced as the landuse Classification card and queryable via /api/landuse/point and /api/landuse/zone.",
    provider: "ESA WorldCover consortium (VITO, Brockmann Consult, CS, GAMMA Remote Sensing, IIASA, Wageningen University) — funded by the European Space Agency",
    resolution: "10 m",
    coverage: "Global",
    temporal: "2021 (v200); a 2020 product (v100) also exists",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Zanaga, D., Van De Kerchove, R., Daems, D., De Keersmaecker, W., et al. (2022). " +
      "ESA WorldCover 10 m 2021 v200. https://doi.org/10.5281/zenodo.7254221",
    url: "https://esa-worldcover.org/",
    notes: (
      <>
        The <strong>current-state land cover</strong> half of dLUC detection — paired with Hansen's
        year-2000 baseline it flags post-baseline transitions (forest → cropland, forest → settlement).
        Built from Sentinel-1 and Sentinel-2; the 3°×3° tiles are merged at ingest by{" "}
        <code>update_landuse</code>.
      </>
    ),
  },
  {
    name: "Harmonized Global Biomass (HGB, 2010)",
    what: "Above-ground live biomass density in megagrams per hectare (Mg/ha, dry-matter) for the year 2010. Multiply by the IPCC default carbon fraction (0.47) for above-ground biomass carbon (tC/ha). Ingested as a COG; surfaced via /api/landuse/point and /api/landuse/zone.",
    provider: "Spawn & Gibbs (University of Wisconsin–Madison) — archived at ORNL DAAC; mirrored on the Microsoft Planetary Computer",
    resolution: "300 m",
    coverage: "Global",
    temporal: "Year 2010 snapshot",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Spawn, S. A., Sullivan, C. C., Lark, T. J., & Gibbs, H. K. (2020). " +
      "Harmonized global maps of above and belowground biomass carbon density in the year 2010. " +
      "Scientific Data, 7, 112. https://doi.org/10.1038/s41597-020-0444-4",
    url: "https://doi.org/10.3334/ORNLDAAC/1763",
    notes: (
      <>
        The headline <strong>biomass carbon-stock reference</strong> for dLUC accounting on
        forest-to-non-forest transitions (§6.4.9). For a higher-tier, location-specific biomass estimate,
        the Meta canopy-height proxy below can stand in for HGB's default densities.
      </>
    ),
  },
  {
    name: "Meta / WRI Canopy Height Map (Tolan et al. 2024)",
    what: "1 m global canopy-height map derived from high-resolution RGB imagery. Read live (not ingested) and exposed at /api/landuse/extras as a point sample — an optional higher-tier proxy for above-ground biomass.",
    provider: "Data for Good at Meta + World Resources Institute (WRI)",
    resolution: "1 m",
    coverage: "Global, where canopy exists — tiles are published only where there is meaningful vegetation",
    temporal: "Imagery composite ~2009 – 2020; published 2024",
    credibility: "Peer-reviewed / authoritative",
    citation:
      "Tolan, J., Yang, H.-I., Nosarzewski, B., Couairon, G., et al. (2024). " +
      "Very high resolution canopy height maps from RGB imagery using self-supervised vision transformer " +
      "and convolutional decoder trained on aerial lidar. Remote Sensing of Environment, 300, 113888. " +
      "https://doi.org/10.1016/j.rse.2023.113888",
    url: "https://dataforgood.facebook.com/dfg/tools/canopy-height-maps",
    notes: (
      <>
        <strong>Not stored locally.</strong> The API reads the published tiles directly over HTTP
        byte-range (<code>/vsicurl/</code>) at request time, so this layer depends on the upstream bucket
        being reachable. A &quot;no canopy here&quot; result is the normal answer outside forest, not a
        failure. Served only at <code>/api/landuse/extras</code>.
      </>
    ),
  },
  {
    name: "GADM",
    what: "Administrative boundaries (countries → sub-national levels). Used to resolve which administrative unit contains a given point, to define polygons for zonal statistics, and to anchor the admin-level fallbacks in the soil composites.",
    provider: "GADM project (Robert J. Hijmans et al., UC Davis)",
    resolution: "Vector polygons; granularity varies per country",
    coverage: "Global. ADM_5 exists only for some countries (notably France); other countries top out at ADM_2..ADM_4.",
    temporal: "Snapshot dataset; new versions every few years (4.0, 4.1, …). The ingest command auto-discovers the newest available release.",
    credibility: "Widely used / community standard",
    url: "https://gadm.org/",
    notes: (
      <>
        Granularity is uneven by country. The resolver returns the most-granular boundary containing the
        point, capped by the <code>withLevel</code> parameter, and falls back to ocean (404) when no boundary
        applies. Boundaries are <strong>not</strong> authoritative for disputed regions; if your modelling
        needs legally-binding boundaries, defer to the relevant national cadastre.
      </>
    ),
  },
  {
    name: "Cloud-Optimized GeoTIFF (COG)",
    what: "On-disk format used for all raster layers — enables HTTP-range reads, so the API only fetches the bytes covering a queried point or zone, even when the source is S3.",
    provider: "OGC standard (since v1.0, 2023)",
    resolution: "n/a (specification)",
    coverage: "n/a",
    temporal: "n/a",
    credibility: "Specification",
    url: "https://www.cogeo.org/",
    notes: (
      <>
        All rasters are written with internal tiling + overviews and deflate compression by{" "}
        <code>rio-cogeo</code>. The same code path works on local disk and on S3 via GDAL's{" "}
        <code>/vsis3/</code> driver — flip <code>AGROREF_STORAGE_BACKEND</code> to <code>s3</code> and the API
        won't notice.
      </>
    ),
  },
];

const CREDIBILITY_TAG: Record<Credibility, TagKind> = {
  "Peer-reviewed / authoritative": "completed",
  "Widely used / community standard": "planned",
  "Specification": "imported",
};

function SourceCard({ s }: { s: Source }) {
  return (
    <article className="rounded-xs border-[1.2px] border-mef-separator bg-white p-6 shadow-mef-card">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="mef-h1">{s.name}</h3>
        <Tag kind={CREDIBILITY_TAG[s.credibility]}>{s.credibility}</Tag>
      </header>

      <p className="mb-4 mef-body text-black/80">{s.what}</p>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <div>
          <dt className="mef-small-caption">Provider</dt>
          <dd className="mt-0.5 text-[13px] leading-4 text-black/80">{s.provider}</dd>
        </div>
        <div>
          <dt className="mef-small-caption">Coverage</dt>
          <dd className="mt-0.5 text-[13px] leading-4 text-black/80">{s.coverage}</dd>
        </div>
        <div>
          <dt className="mef-small-caption">Resolution</dt>
          <dd className="mt-0.5 text-[13px] leading-4 text-black/80">{s.resolution}</dd>
        </div>
        <div>
          <dt className="mef-small-caption">Temporal</dt>
          <dd className="mt-0.5 text-[13px] leading-4 text-black/80">{s.temporal}</dd>
        </div>
      </dl>

      {s.notes && (
        <div className="mt-4 rounded-xs border-[1.2px] border-mef-separator bg-mef-subtle p-3 text-[13px] leading-4 text-black/80">
          {s.notes}
        </div>
      )}

      {s.citation && (
        <blockquote className="mt-4 border-l-2 border-mef-gray pl-3 text-[11px] leading-4 italic text-black/60">
          {s.citation}
        </blockquote>
      )}

      <a
        href={s.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.04em] text-mef-green-text hover:underline mef-focus-ring"
      >
        Open source <ExternalLink className="h-3 w-3" />
      </a>
    </article>
  );
}

const SOURCE_LABELS: Array<{ id: number; label: string }> = [
  { id: 0, label: "Country average" },
  { id: 1, label: "Region average (ADM_1)" },
  { id: 2, label: "District average (ADM_2)" },
  { id: 3, label: "Municipality average (ADM_3)" },
  { id: 4, label: "Local average (ADM_4)" },
  { id: 5, label: "Sub-local average (ADM_5)" },
  { id: 6, label: "High-resolution survey (INRAE WMS)" },
];

export default function DocsPage() {
  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-6 py-8">
      <section>
        <h1 className="mef-h1">Data sources &amp; credibility</h1>
        <p className="mt-2 max-w-3xl mef-body text-black/60">
          Every layer queryable through this API is derived from one of the sources below. The intent
          is that anyone building a model on top of <code>mef-agroref</code> can audit the provenance and
          methodology of each input before publishing results. Licensing and re-use terms for each source
          are tracked in the repository <code>README</code>.
        </p>
      </section>

      <div className="space-y-5">
        {SOURCES.map((s) => (
          <SourceCard key={s.name} s={s} />
        ))}
      </div>

      <section className="rounded-xs border-[1.2px] border-mef-separator bg-white p-6 shadow-mef-card">
        <h3 className="mef-h1">Source &amp; provenance for soil layers</h3>
        <p className="mt-2 mef-body text-black/80">
          Some soil layers — notably CaCO3 — are <strong>composite rasters</strong>: a single COG combining a
          global SoilGrids/admin-average fallback with a high-resolution overlay where one is available
          (France, via INRAE WMS). Each pixel therefore has a provenance, which the API reports on point
          queries as the <code>source</code> field in the response.
        </p>

        <p className="mt-4 mef-body text-black/80">
          The composite is a 2-band GeoTIFF: band 1 carries the value, band 2 carries an integer source ID
          mapped through this table:
        </p>

        <table className="mt-3 w-full text-[13px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="w-16 py-1 font-medium">ID</th>
              <th className="py-1 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {SOURCE_LABELS.map((s) => (
              <tr key={s.id} className="border-t border-mef-surface-2">
                <td className="py-1.5 font-mono text-black/60">{s.id}</td>
                <td className="py-1.5 text-black/80">{s.label}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="mt-4 mef-body text-black/80">
          Reading order during the bake: SoilGrids (or admin-level point averages) is laid down first at
          coarser levels, then progressively overwritten by finer levels. Finally any regional high-res
          overlay (e.g. INRAE for France) wins — that's where source id 6 comes from. Zonal statistics use
          band 1 only — a polygon average of provenance IDs isn't meaningful.
        </p>
      </section>

      <section className="rounded-xs border-[1.2px] border-mef-separator bg-white p-6 shadow-mef-card">
        <h3 className="mef-h1">Classifications — IPCC vs CFT/CFP</h3>
        <p className="mt-2 mef-body text-black/80">
          Two parallel categorical schemes live alongside the continuous layers, both queryable through
          the same <code>/api/point</code> and <code>/api/zone</code> endpoints. They surface in the
          frontend's <strong>Classifications</strong> card. Pick the scheme your downstream consumer
          expects:
        </p>

        <table className="mt-4 w-full text-[13px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="w-1/3 py-1 font-medium">Use case</th>
              <th className="py-1 font-medium">Scheme</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80">SOC inventory / academic / IPCC-Tier-1 reporting</td>
              <td className="py-1.5 text-black/80">
                <code>ipcc_climate</code> (12 zones) + <code>ipcc_soil</code> (8 classes incl.
                &quot;Other Areas&quot;) — the JRC ESDAC rasters, peer-reviewed methodology.
              </td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80">Cool Farm Tool / Cool Farm Platform integration</td>
              <td className="py-1.5 text-black/80">
                <code>cft_climate</code> (10 farming zones) + <code>cft_soil</code> (6 classes) —
                labels match CFA's docs verbatim. Polar pixels (CFT 11/12) carry IPCC labels for
                completeness.
              </td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80">Showing both to the user</td>
              <td className="py-1.5 text-black/80">
                Both layers are independent — the API returns whichever is registered in the catalog.
                The frontend renders all four rows in its <em>Classifications</em> card; both schemes
                resolve to the same human-readable label in 99% of locations.
              </td>
            </tr>
          </tbody>
        </table>

        <p className="mt-4 mef-body text-black/80">
          Both layers are categorical (uint8 with a fixed integer→label dictionary stored on the
          <code> DataLayer.categories</code> column). On point queries the API returns the integer
          class ID plus its label; on zonal queries it returns the <em>modal</em> class (most-common
          pixel value) plus its label — a mean of class IDs would be meaningless.
        </p>
      </section>

      <section className="rounded-xs border-[1.2px] border-mef-separator bg-white p-6 shadow-mef-card">
        <h3 className="mef-h1">Land use &amp; forest change (ISO 14067 dLUC)</h3>
        <p className="mt-2 mef-body text-black/80">
          The <code>landuse</code> layers exist to answer one question for the carbon-footprint toolchain
          downstream: <strong>did this land change use, when, and how much carbon did that cost?</strong>{" "}
          Four sources combine to cover the ISO 14067 §6.4.9 direct land-use-change (dLUC) inputs — and the
          same layers back EUDR deforestation-cutoff checks. Each plays a distinct role:
        </p>

        <table className="mt-4 w-full text-[13px]">
          <thead>
            <tr className="text-left mef-small-caption">
              <th className="w-1/4 py-1 font-medium">Source / layer</th>
              <th className="py-1 font-medium">Role in the dLUC calculation</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80"><code>Hansen treecover_2000_pct</code></td>
              <td className="py-1.5 text-black/80">
                Reference-year (2000) forest <strong>baseline</strong> — inside the 20-year amortisation
                window for any footprint computed through 2025.
              </td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80"><code>Hansen lossyear</code></td>
              <td className="py-1.5 text-black/80">
                <strong>When</strong> each pixel lost forest (2001 – 2023). <code>/api/landuse/zone</code>{" "}
                returns a per-year loss histogram plus EUDR-cutoff aggregates over the admin polygon.
              </td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80"><code>ESA WorldCover</code></td>
              <td className="py-1.5 text-black/80">
                <strong>Current</strong> land cover — what the pixel is today, so a forest → cropland /
                settlement transition can be classified.
              </td>
            </tr>
            <tr className="border-t border-mef-surface-2">
              <td className="py-1.5 text-black/80"><code>HGB</code> (+ optional <code>Meta CHM</code>)</td>
              <td className="py-1.5 text-black/80">
                <strong>How much carbon</strong> — above-ground biomass density at the loss location (×0.47
                for AGB-C). Meta canopy height is the higher-tier proxy when HGB's defaults aren't precise
                enough.
              </td>
            </tr>
          </tbody>
        </table>

        <p className="mt-4 mef-body text-black/80">
          <code>/api/landuse/point</code> and <code>/api/landuse/zone</code> can also return the forest{" "}
          <em>geometry</em> — stable forest (canopy present in 2000 and never lost) and post-2000 regrowth —
          as GeoJSON when you pass <code>?include_geometry=true</code> (off by default to keep responses
          small). Every one of these layers carries an <code>iso14067_role</code> tag in the response{" "}
          <code>provenance</code> (e.g. <code>dLUC_forest_baseline</code>,{" "}
          <code>dLUC_forest_loss_history</code>, <code>land_cover_current_state</code>,{" "}
          <code>biomass_carbon_stock_reference</code>), and the whole catalog rolls up under{" "}
          <code>/api/audit/manifest</code> for §6.4.9 / §6.4.10 verification.
        </p>
      </section>

      <section className="rounded-xs border-[1.2px] border-mef-yellow bg-mef-yellow-tint p-4 mef-body text-black/80">
        <h3 className="mef-body-med mb-1 uppercase tracking-[0.02em]">What this dataset is not</h3>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            Not a substitute for on-farm measurements — TerraClimate and SoilGrids have known biases at
            fine scales, especially in mountainous or heterogeneous terrain.
          </li>
          <li>
            GADM boundaries are not authoritative for legal or cadastral use; treat them as analytical units.
          </li>
          <li>
            Aggregations to admin polygons average across the full polygon — pair with land-cover masking
            (e.g. the French RPG agricultural parcel layer) for agronomically meaningful values.
          </li>
        </ul>
      </section>
    </div>
  );
}
